/**
 * 订单/投保相关类型定义
 */

/** 性别 */
export enum Gender {
  Female = "FEMALE",
  Male = "MALE",
  Unknown = "UNKNOWN",
}

/** 身份证明文件类型 */
export enum IdType {
  BirthCertificate = "BIRTH_CERTIFICATE",
  HkMacauPass = "HK_MACAU_PASS",
  HongkongID = "HONGKONG_ID",
  IDCard = "CHINA_ID",
  MacaoID = "MACAO_ID",
  Passport = "PASSPORT",
  TaiwanID = "TAIWAN_ID",
  Unknown = "UNKNOWN",
}

/** Beneficiary 关系 */
export type OrderEffectivePolicyholderVO = {
  chineseName?: string;
  email?: string;
  englishName?: string;
  idNumber?: string;
  idType?: IdType;
  phone?: string;
  [property: string]: any;
};

export enum BeneficiaryRelationship {
  Brother = "BROTHER",
  BrotherAndSister = "BROTHER_AND_SISTER",
  Child = "CHILD",
  Employee = "EMPLOYEE",
  Fiance = "FIANCE",
  Fiancee = "FIANCEE",
  GrandparentOrGrandchild = "GRANDPARENT_OR_GRANDCHILD",
  LegalGuardian = "LEGAL_GUARDIAN",
  Other = "OTHER",
  Parent = "PARENT",
  Self = "SELF",
  Sister = "SISTER",
  SisterAndBrother = "SISTER_AND_BROTHER",
  Spouse = "SPOUSE",
  Trustee = "TRUSTEE",
  Trustor = "TRUSTOR",
  Unidentified = "UNIDENTIFIED",
  Unknown = "UNKNOWN",
}

/** 家庭成员关系 */
export enum FamilyMemberRelationship {
  Child = "CHILD",
  Father = "FATHER",
  Mother = "MOTHER",
  Spouse = "SPOUSE",
  Unknown = "UNKNOWN",
}

/** 就业类型 */
export enum TypeEnum {
  FullTime = "FULL_TIME",
  PartTime = "PART_TIME",
  SelfEmployed = "SELF_EMPLOYED",
  Unknown = "UNKNOWN",
  UnpaidPerson = "UNPAID_PERSON",
}

/** 就业类型（新版） */
export enum WorkType {
  FullTime = "FULL_TIME",
  PartTime = "PART_TIME",
  SelfEmployed = "SELF_EMPLOYED",
  Unknown = "UNKNOWN",
  UnpaidPerson = "UNPAID_PERSON",
}

/** 教育程度 */
export enum Education {
  HighSchool = "HIGH_SCHOOL",
  Primary = "PRIMARY",
  Secondary = "SECONDARY",
  TertiaryOrAbove = "TERTIARY_OR_ABOVE",
  University = "UNIVERSITY",
  Unknown = "UNKNOWN",
}

/** 婚姻状态（新版，区别于现有 MaritalStatus） */
export enum Marriage {
  Divorced = "DIVORCED",
  Married = "MARRIED",
  Single = "SINGLE",
  Unknown = "UNKNOWN",
  Widowed = "WIDOWED",
}

/** 预期退休年龄 */
export enum IntendedRetirementAge {
  Age65 = "AGE_65",
  Others = "OTHERS",
  Retired = "RETIRED",
}

/** 医疗保险类别 */
export enum MedicalInsuranceType {
  Group = "GROUP",
  Individual = "INDIVIDUAL",
}

/** 入境香港证件类型 */
export enum HkEntryIdType {
  Passport = "PASSPORT",
  TempPassport = "TEMP_PASSPORT",
  Unknown = "UNKNOWN",
}

/** 订单审核状态 */
export enum OrderAuditStatus {
  Approved = "APPROVED",
  ConfirmationPendingReview = "CONFIRMATION_PENDING_REVIEW",
  Initial = "INITIAL",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingModification = "PENDING_MODIFICATION",
  PendingModificationRejected = "PENDING_MODIFICATION_REJECTED",
  PendingReview = "PENDING_REVIEW",
  Rejected = "REJECTED",
}

/**
 * 订单归属
 */
export enum OrderBelong {
  Order = "ORDER",
  Policy = "POLICY",
}

/**
 * 订单状态
 */
export enum OrderStatus {
  Completed = "COMPLETED",
  ComplianceAudit = "COMPLIANCE_AUDIT",
  Draft = "DRAFT",
  Expired = "EXPIRED",
  OrderAudit = "ORDER_AUDIT",
  PendingAllocation = "PENDING_ALLOCATION",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingSignature = "PENDING_SIGNATURE",
}

/**
 * 保单状态
 */
export enum PolicyStatus {
  Checking = "CHECKING",
  Approving = "APPROVING",
  PendingPayment = "PENDING_PAYMENT",
  Paid = "PAID",
  Effective = "EFFECTIVE",
  Denied = "DENIED",
  UnderwritingDelayed = "UNDERWRITING_DELAYED",
  CoolingOffPeriodCancel = "COOLING_OFF_PERIOD_CANCEL",
  NonCoolingOffPeriodCancel = "NON_COOLING_OFF_PERIOD_CANCEL",
  PolicyLapsed = "POLICY_LAPSED",
  TransferredOut = "TRANSFERRED_OUT",
  TransferredIn = "TRANSFERRED_IN",
  Terminated = "TERMINATED",
  Canceled = "CANCELED",
  OrderInvalidated = "ORDER_INVALIDATED",
}

/**
 * 合规审核状态
 */
export enum RiskAssessmentAuditStatus {
  Initial = "INITIAL",
  PendingSubmission = "PENDING_SUBMISSION",
  PendingModificationCra = "PENDING_MODIFICATION_CRA",
  Submitted = "SUBMITTED",
  Rejected = "REJECTED",
  Approved = "APPROVED",
}
/**
 * 保单文件状态
 */
export enum PolicyFileStatus {
  NotUploaded = "NOT_UPLOADED",
  Uploaded = "UPLOADED",
}
/**
 * AML审核状态
 */
export enum AmlAuditStatus {
  Initial = "INITIAL",
  NoConcern = "NO_CONCERN",
  PendingQuery = "PENDING_QUERY",
  PendingReview = "PENDING_REVIEW",
  WithConcern = "WITH_CONCERN",
}
/**
 * CRA風險等級
 */
export enum RiskClassResult {
  HighRisk = "HIGH_RISK",
  LowRisk = "LOW_RISK",
  MediumRisk = "MEDIUM_RISK",
}

export const ORDER_STATUS_DICT: Record<OrderStatus, string> = {
  [OrderStatus.Completed]: "已完成",
  [OrderStatus.Draft]: "草稿",
  [OrderStatus.Expired]: "已失效",
  [OrderStatus.PendingAllocation]: "待分配",
  [OrderStatus.PendingConfirmation]: "待確認",
  [OrderStatus.PendingSignature]: "待簽署",
  [OrderStatus.OrderAudit]: "訂單審核",
  [OrderStatus.ComplianceAudit]: "合規審核",
};

// PolicyStatus
export const POLICY_STATUS_DICT: Record<PolicyStatus, string> = {
  [PolicyStatus.Checking]: "檢查中",
  [PolicyStatus.Approving]: "核保中",
  [PolicyStatus.PendingPayment]: "待繳費",
  [PolicyStatus.Paid]: "已繳費",
  [PolicyStatus.Effective]: "已生效",
  [PolicyStatus.Denied]: "拒保",
  [PolicyStatus.UnderwritingDelayed]: "核保延期",
  [PolicyStatus.CoolingOffPeriodCancel]: "冷静期内退保",
  [PolicyStatus.NonCoolingOffPeriodCancel]: "冷静期外退保",
  [PolicyStatus.PolicyLapsed]: "保單失效",
  [PolicyStatus.TransferredOut]: "轉出",
  [PolicyStatus.TransferredIn]: "轉入",
  [PolicyStatus.Terminated]: "終止",
  [PolicyStatus.Canceled]: "取消投保",
  [PolicyStatus.OrderInvalidated]: "作廢訂單",
};

// OrderAuditStatus
export const ORDER_AUDIT_STATUS_DICT: Record<OrderAuditStatus, string> = {
  [OrderAuditStatus.Approved]: "已通过",
  [OrderAuditStatus.ConfirmationPendingReview]: "客戶確認修改無誤-待審核",
  [OrderAuditStatus.Initial]: "初始化",
  [OrderAuditStatus.PendingConfirmation]: "待確認（業務代表發起訂單修改）",
  [OrderAuditStatus.PendingModification]: "Pending/補充或修改資料",
  [OrderAuditStatus.PendingModificationRejected]: "客戶拒絕-待修改",
  [OrderAuditStatus.PendingReview]: "待審核",
  [OrderAuditStatus.Rejected]: "訂單審核拒絕",
};
// riskAssessmentAuditStatus
export const RISK_ASSESSMENT_AUDIT_STATUS_DICT: Record<
  RiskAssessmentAuditStatus,
  string
> = {
  [RiskAssessmentAuditStatus.Initial]: "初始化",
  [RiskAssessmentAuditStatus.PendingSubmission]: "待提交CRA",
  [RiskAssessmentAuditStatus.Submitted]: "已提交",
};

/** 列表排序方向 */
export enum OrderAspect {
  Asc = "ASC",
  Desc = "DESC",
}

export enum Type {
  Company = "COMPANY",
  Individual = "INDIVIDUAL",
}

export enum MaritalStatus {
  Married = "MARRIED",
  Single = "SINGLE",
  Divorced = "DIVORCED",
  Widowed = "WIDOWED",
  Unknown = "UNKNOWN",
}

export enum ComplianceAuditStatus {
  Approved = "APPROVED",
  FirstApproved = "FIRST_APPROVED",
  FirstRejected = "FIRST_REJECTED",
  Initial = "INITIAL",
  Pending = "PENDING",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingModification = "PENDING_MODIFICATION",
  Rejected = "REJECTED",
  SecondApproved = "SECOND_APPROVED",
  SecondRejected = "SECOND_REJECTED",
  NotApproved = "NOT_APPROVED",
}

export enum ComplianceRecheckStatus {
  Approved = "APPROVED",
  NotApproved = "NOT_APPROVED",
  Rejected = "REJECTED",
}

export enum City {
  HongKong = "HongKong",
  Other = "OTHER",
}

export enum PaymentMethod {
  UNKNOWN = "UNKNOWN",
  CASH = "CASH",
  CHEQUE = "CHEQUE",
  TELEGRAPHIC_TRANSFER = "TELEGRAPHIC_TRANSFER",
  CASH_ORDER = "CASH_ORDER",
  BANK_TRANSFER = "BANK_TRANSFER",
  CREDIT_CARD = "CREDIT_CARD",
  UNION_PAY = "UNION_PAY",
  BANK_CASH_RECEIPTS = "BANK_CASH_RECEIPTS",
  BANK_AUTO_TRANSFER = "BANK_AUTO_TRANSFER",
  DELAY_PAYMENT = "DELAY_PAYMENT",
  OTHER = "OTHER",
}

export enum PaymentFrequency {
  SinglePayment = "SINGLE_PAYMENT",
  Annually = "ANNUALLY",
  SemiAnnually = "SEMI_ANNUALLY",
  Quarterly = "QUARTERLY",
  Monthly = "MONTHLY",
}

export enum DividendDistributionMethod {
  InterestAccumulation = "INTEREST_ACCUMULATION",
  ToBePaidInCash = "TO_BE_PAID_IN_CASH",
  ToReducePremium = "TO_REDUCE_PREMIUM",
  ToPurchasePaidUpAddition = "TO_PURCHASE_PAID_UP_ADDITION",
}

// Interfaces

export interface BasicInfo {
  birthDate?: string;
  chineseGivenName?: string;
  chineseSurname?: string;
  englishGivenName?: string;
  englishSurname?: string;
  gender?: Gender;
  maritalStatus?: MaritalStatus;
  idNumber?: string;
  idType?: IdType;
  salutation?: Gender;
  [property: string]: any;
}

export interface PersonalInfo {
  address?: string;
  country?: string;
  education?: Education;
  email?: string;
  height?: number;
  marriage?: Marriage;
  phone?: string;
  salutation?: Gender;
  smokingQuantity?: number;
  weight?: number;
  smoke?: boolean;
  /**
   * 住宅地址-國家/地區
   */
  residentialAddressRegion?: string;
  /**
   * 完整住宅地址
   */
  residentialAddress?: string;
  [property: string]: any;
}

export interface Employer {
  address?: string;
  industry?: string;
  name?: string;
  [property: string]: any;
}

export interface WorkInfo {
  employer?: Employer;
  positionAndDuties?: string;
  intendedRetirementAge?: IntendedRetirementAge | string;
  intendedRetirementAgeOther?: string;
  jobTitle?: string;
  type?: TypeEnum | WorkType;
  [property: string]: any;
}

export interface TaxInfo {
  countryOfBirth?: string;
  countryOfResidence?: string;
  hasHKIDCard?: boolean;
  hasOtherTaxResidency?: string;
  hkEntryIdNumber?: string;
  hkEntryIdType?: HkEntryIdType;
  isHKPermanentResident?: string;
  isUSCitizen?: string;
  nationality?: string;
  remark?: string;
  [property: string]: any;
}

export interface FamilyMember {
  age?: number;
  id?: number;
  isDependent?: String;
  relationship?: FamilyMemberRelationship;
  [property: string]: any;
}

export interface PolicyholderOther {
  isVulnerableCustomer?: boolean;
  isProfessionalInvestor?: boolean;
  remark?: string;
  witnessName?: string;
  witnessIdType?: string;
  witnessIdNumber?: string;
  [property: string]: any;
}

export interface PolicyholderVO {
  basic?: BasicInfo;
  familyMembers?: FamilyMember[];
  isProfessionalInvestor?: boolean; // Deprecated: use other.isProfessionalInvestor
  policyholderId?: number;
  isVulnerableCustomer?: boolean; // Deprecated: use other.isVulnerableCustomer
  personal?: PersonalInfo;
  tax?: TaxInfo;
  work?: WorkInfo;
  relationship?: BeneficiaryRelationship;
  remark?: string; // Deprecated: use other.remark
  other?: PolicyholderOther;
  sourceId?: number;
  [property: string]: any;
}

// Alias for PolicyholderDTO used in api/order.ts
export type PolicyPerson = PolicyholderVO;

export interface InsuredOther {
  remark?: string;
  [property: string]: any;
}

export interface InsuredDTO {
  basic?: BasicInfo;
  personal?: PersonalInfo;
  relationship?: BeneficiaryRelationship;
  tax?: TaxInfo;
  work?: WorkInfo;
  draft?: boolean;
  submit?: boolean;
  insuredId?: number;
  orderId?: number;
  remark?: string; // Deprecated: use other.remark
  other?: InsuredOther;
  sourceId?: number;
  [property: string]: any;
}

// Alias for new API response
export type InsuredVO = InsuredDTO;

export interface BeneficiaryOther {
  remark?: string;
  [property: string]: any;
}

export interface BeneficiaryVO {
  basic?: BasicInfo;
  beneficiaryId?: number;
  relationship?: BeneficiaryRelationship;
  percentage?: number;
  isLegalBeneficiary?: boolean;
  draft?: boolean;
  submit?: boolean;
  orderId?: number;
  remark?: string; // Deprecated: use other.remark
  other?: BeneficiaryOther;
  sourceId?: number;
  [property: string]: any;
}

export interface RiderVO {
  contributionYears?: string;
  currency?: string;
  paymentFrequency?: string;
  premiumPerPeriod?: number;
  productCode?: string;
  productName?: string;
  protectionPeriod?: string;
  sumAssured?: number;
  [property: string]: any;
}

export interface OrderProductPlanVO {
  productPlanId: string;
  applySpecialFirstYearDiscount?: boolean;
  commissionRate?: number;
  companyCode?: string;
  companyName?: string;
  contributionYears?: string;
  currency?: string;
  dividendDistributionMethod?: string;
  firstPaymentMethod?: string;
  firstYearDiscountedPremium?: number;
  isDdaSupported?: boolean;
  isIncreasingProtectionIncluded?: boolean;
  isPolicyBackdated?: boolean;
  isPremiumFinanced?: boolean;
  isPrepaid?: boolean;
  paymentFrequency?: string;
  policyBackDate?: string;
  policyLanguage?: string;
  premiumPerPeriod?: number;
  prepaidAmountInclFirst?: number;
  productCode?: string;
  productName?: string;
  protectionPeriod?: string;
  region?: string;
  renewalPaymentMethod?: string;
  riders?: RiderVO[];
  specialFirstYearDiscountRate?: number;
  sumAssured?: number;
  [property: string]: any;
}

export interface OrderSignInfo {
  advisorSignData?: string;
  city?: City;
  photo?: string;
  policyholderSignData?: string;
  signatures?: Signature[];
  signatureVersion?: string;
  [property: string]: any;
}

export interface ChooseProduct {
  choose?: boolean;
  productCode?: string;
  productName?: string;
  region?: string;
  companyCode?: string;
  companyName?: string;
  [property: string]: any;
}

export interface CurrentAssets {
  cashAndDeposits?: number;
  currentLiquidAssets?: number;
  expectedRetireLiquidAssets?: number;
  hasOtherCurrentAssets?: boolean;
  liquidInvestments?: number;
  otherCurrentAssetsAmount?: number;
  otherCurrentAssetsName?: string;
  [property: string]: any;
}

export interface MonthlyExpense {
  avgMonthlyExpense?: number;
  hasOtherExpense?: boolean;
  insuranceContribution?: number;
  mortgageLoanRepayment?: number;
  otherExpenseAmount?: number;
  otherExpenseName?: string;
  personalAndFamilyExpense?: number;
  [property: string]: any;
}

export interface MonthlyIncome {
  avgMonthlyIncome?: number;
  expectedRetireIncome?: number;
  hasOtherIncome?: boolean;
  otherIncomeAmount?: number;
  otherIncomeName?: string;
  rentalIncome?: number;
  salaryCommissionBonus?: number;
  [property: string]: any;
}

export interface Liabilities {
  hasOtherLiabilities?: boolean;
  mortgageLoan?: number;
  otherLiabilityAmount?: number;
  otherLiabilityName?: string;
  personalCreditCardLoan?: number;
  [property: string]: any;
}

export interface NonCurrentAssets {
  otherNonCurrentAssets?: number;
  property?: number;
  [property: string]: any;
}

export interface CurrentProtection {
  accidentInsurance?: number;
  attachmentRemarks?: string;
  criticalIllnessInsurance?: number;
  lifeInsurance?: number;
  medicalInsuranceAmount?: number;
  medicalInsuranceType?: MedicalInsuranceType;
  otherExistingProtection?: string;
  [property: string]: any;
}

export interface PremiumFinancing {
  insuranceCompany?: string;
  lenderName?: string;
  loanAmount?: string;
  loanInterestRate?: string;
  loanTerm?: string;
  policyNo?: string;
  remark?: string;
  repaymentAmount?: string;
  [property: string]: any;
}

export interface OrderFinancialVO {
  /**
   * 資產及負債
   */
  assetsAndLiabilities?: number;
  /**
   * 推荐的保險產品
   */
  chooseProducts?: ChooseProduct[];
  /**
   * 流动资产
   */
  currentAssets?: CurrentAssets;
  /**
   * 估計每月盈餘/超支
   */
  estimatedMonthlyProfitOrLoss?: number;
  /**
   * 过去2年的平均每月支出
   */
  expense?: MonthlyExpense;
  /**
   * FNA
   */
  financialAnalysis?: FormData[];
  financialId?: number;
  /**
   * FNA 版本
   */
  fnaVersion?: string;
  /**
   * 是否需要填寫財務資料
   */
  isFinancialInfoRequired?: boolean;
  /**
   * 閣下是否有任何現有的保單被用作"保費融資"的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品
   */
  hasExistingPolicyAsMortgage?: boolean;
  /**
   * 过去2年的平均每月收入
   */
  income?: MonthlyIncome;
  /**
   * 閣下是否打算運用"保費融資"安排此保單
   */
  isPremiumFinanced?: boolean;
  /**
   * 负债
   */
  liabilities?: Liabilities;
  /**
   * 非流动资产
   */
  nonCurrentAssets?: NonCurrentAssets;
  /**
   * 是否只能提供一款保险产品
   */
  onlyProvideOneProduct?: boolean;
  /**
   * 是否只能提供一家保險公司建議之原因
   */
  onlyProvideOneProductReason?: string;
  orderId?: number;
  /**
   * 現有保費融資的抵押保單詳情
   */
  premiumFinancing?: PremiumFinancing[];
  /**
   * 保费融资分析
   */
  premiumFinanceAnalysis?: FormData[];
  /**
   * 现行保障
   */
  protection?: CurrentProtection;
  /**
   * 建议及原因
   */
  suggestionsAndReasons?: FormData[];
  /**
   * 转保声明
   */
  transferInsuranceDeclaration?: FormData[];
  draft?: boolean;
  submit?: boolean;
  [property: string]: any;
}
// Main Order Interface (matching Request in orders.ts)
export interface OrderDetail {
  /**
   * 订单ID
   */
  orderId?: number;
  /**
   * 顾问名称
   */
  advisorName?: string;
  /**
   * 顾问团队名称
   */
  advisorTeamName?: string;
  /**
   * 顾问团队ID
   */
  advisorTid?: number;
  /**
   * 渠道信息
   */
  advisorUid?: number;
  /**
   * 受益人ID列表
   */
  beneficiaries?: BeneficiaryVO[];

  complianceAuditStatus?: ComplianceAuditStatus;
  /**
   * 产品信息/保险公司代码
   */
  companyCode?: string;
  /**
   * 保险公司名称
   */
  companyName?: string;
  /**
   * 投受益人信息
   */
  customerId?: number;
  /**
   * 订单财务信息
   */
  financial?: OrderFinancialVO;
  /**
   * 产品信息
   */
  insurance?: string;
  /**
   * 保险公司名称（旧字段）
   */
  insuranceName?: string;
  /**
   * 受保人
   */
  insured?: InsuredDTO;
  /**
   * 受保人类型
   */
  insuredType?: Type;
  /**
   * 保司保单状态
   */
  insurerPolicyStatus?: string;
  /**
   * 是否需要填寫財務資料
   */
  isFinancialInfoRequired?: boolean;
  /**
   * 是否有联合订单是待签署状态
   */
  isUnionExist?: boolean;
  /**
   * 是否法定受益人
   */
  legalBeneficiary?: boolean;
  /**
   * 订单审核状态
   */
  orderAuditStatus?: OrderAuditStatus;
  /**
   * 订单号
   */
  orderNo?: string;
  /**
   * 订单状态
   */
  orderStatus?: OrderStatus;
  /**
   * 订单子状态
   */
  orderSubStatus?: SubStatusCountOrderStatus;
  /**
   * 投保人
   */
  policyholder?: PolicyholderVO;
  /**
   * 投保人类型
   */
  policyholderType?: Type;
  /**
   * 保单号
   */
  policyNo?: string;
  /**
   * 保单状态
   */
  policyStatus?: PolicyStatus;
  /**
   * 产品编码
   */
  productCode?: string;
  /**
   * 产品名称
   */
  productName?: string;
  /**
   * 产品计划列表
   */
  productPlans?: OrderProductPlanVO[];
  referrerId?: number;
  /**
   * 推荐人名称
   */
  referrerName?: string;
  /**
   * 推荐人团队名称
   */
  referrerTeamName?: string;
  /**
   * 推荐人团队ID
   */
  referrerTid?: number;
  /**
   * 推荐人ID
   */
  referrerUid?: number;
  /**
   * 合规审核状态
   */
  riskAssessmentAuditStatus?: RiskAssessmentAuditStatus;
  /**
   * 第二受保人
   */
  secondInsured?: InsuredDTO;
  /**
   * 第二受保人类型
   */
  secondInsuredType?: Type;
  /**
   * 第二投保人
   */
  secondPolicyholder?: PolicyholderVO;
  /**
   * 第二投保人类型
   */
  secondPolicyholderType?: Type;
  /**
   * 签名信息
   */
  signInfo?: OrderSignInfo;
  /**
   * 签名时间
   */
  signTime?: string;
  /**
   * 提交时间
   */
  submitTime?: string;

  productCompleted?: boolean;
  /**
   * 签名是否完成
   */
  signatureCompleted?: boolean;
  /**
   * 预约信息
   */
  appointmentInfo?: OrderAppointmentInfo;
  /**
   * 意向产品列表
   */
  intendedProducts?: OrderIntendedProduct[];
  /**
   * 财务资料是否完成
   */
  financialCompleted?: boolean;
  /**
   * 财务需求分析是否完成
   */
  fnaCompleted?: boolean;
  /**
   * 附件是否完成
   */
  attachmentCompleted?: boolean;
  [property: string]: any;
}

// Aliases for DTOs
export type PolicyholderDTO = PolicyholderVO;
// export type InsuredDTO = InsuredDTO; // Removed alias as it is now the main type
export type BeneficiaryDTO = BeneficiaryVO;
export type FinancialDataDTO = {
  /**
   * 推荐的保險產品
   */
  chooseProducts?: ChooseProduct[];
  /**
   * 流动资产
   */
  currentAssets?: CurrentAssets;
  draft?: boolean;
  /**
   * 过去2年的平均每月支出
   */
  expense?: MonthlyExpense;
  /**
   * FNA
   */
  financialAnalysis?: FormData[];
  financialId?: number;
  /**
   * 閣下是否有任何現有的保單被用作“保費融資”的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品
   */
  hasExistingPolicyAsMortgage?: boolean;
  /**
   * 过去2年的平均每月收入
   */
  income?: MonthlyIncome;
  /**
   * 是否需要填寫財務資料
   */
  isFinancialInfoRequired?: boolean;
  /**
   * 閣下是否打算運用“保費融資”安排此保單
   */
  isPremiumFinanced?: boolean;
  /**
   * 负债
   */
  liabilities?: Liabilities;
  /**
   * 非流动资产
   */
  nonCurrentAssets?: NonCurrentAssets;
  /**
   * 是否只能提供一款保险产品
   */
  onlyProvideOneProduct?: boolean;
  /**
   * 是否只能提供一家保險公司建議之原因
   */
  onlyProvideOneProductReason?: string;
  orderId?: number;
  /**
   * 現有保費融資的抵押保單詳情
   */
  premiumFinancing?: PremiumFinancing[];
  /**
   * 现行保障
   */
  protection?: CurrentProtection;
  /**
   * 是否提交
   */
  submit?: boolean;
  /**
   * 建议及原因
   */
  suggestionsAndReasons?: FormData[];
  /**
   * 转保声明
   */
  transferInsuranceDeclaration?: FormData[];
  [property: string]: any;
};

export interface OrderBasicDTO {
  /**
   * 受益人
   */
  beneficiaries?: BeneficiaryDTO[];
  draft?: boolean;
  submit?: boolean;
  /**
   * 财务资料
   */
  financialData?: FinancialDataDTO;
  /**
   * 受保人
   */
  insured?: InsuredDTO;
  orderId?: number;
  /**
   * 投保人
   */
  policyholder?: PolicyholderDTO;
  /**
   * 业务资料
   */
  referrerUid?: number;
  secondInsured?: InsuredDTO;
  secondPolicyholder?: PolicyholderDTO;
}
export type OrderListItem = OrderDetail;

export interface OrderListRequest {
  pageNum?: number;
  pageSize?: number;
  orderColumn?: string;
  orderAspect?: OrderAspect;
  insurerPolicyStatus?: string;
  insurerPolicyStatusList?: string[];
  orderMainStatus?: DatumOrderStatus;
  orderSubStatus?: SubStatusCountOrderStatus;
  orderStatusSearch?: string;
  orderNo?: string;
  policyFileUploadStatus?: PolicyFileStatus;
  [property: string]: any;
}

export interface OrderListResponse {
  content?: OrderDetail[];
  total?: number;
  [key: string]: any;
}

export interface CreateBasicOrderRequest {
  [key: string]: any;
}

export interface CreateBasicOrderResponse {
  /**
   * 交叉销售
   */
  isCrossSellingExist?: boolean;
  /**
   * 订单ID
   */
  orderId?: number;
  /**
   * 投保人ID
   */
  policyholderId?: number;
  /**
   * 已存在的客户信息（用于重复提示）
   */
  existingCustomer?: {
    chineseSurname?: string;
    chineseGivenName?: string;
    idType?: string;
    idNumber?: string;
  };
  [key: string]: any;
}

/**
 * 附件项
 */
export interface AttachmentItem {
  files?: File[];
  max?: number;
  name?: string;
  required?: boolean;
  type?: string;
  [property: string]: any;
}
export interface File {
  attachmentId?: number;
  contentType?: string;
  createTime?: string;
  creatorName?: string;
  name?: string;
  ossPath?: string;
  path?: string;
  size?: number;
  thumbnail?: string;
  [property: string]: any;
}

/**
 * 订单附件配置
 */
export interface OrderAttachmentConfig {
  condition?: string;
  items?: AttachmentItem[];
  name?: string;
  key?: string;
  required?: boolean;
  type?: string;
  [property: string]: any;
}

/**
 * 签名类型
 */
export enum SignatureType {
  Advisor = "ADVISOR",
  Insured = "INSURED",
  Policyholder = "POLICYHOLDER",
  Witness = "WITNESS",
}

export interface SignatureConfigItem {
  name: string;
  required: boolean;
  condition?: any;
  formType?: string;
  sign: {
    type: SignatureType;
    condition?: any;
  }[];
  _fileUrl?: string;
  // properties for compatibility with OrderAttachmentConfig if needed
  key?: string;
  type?: string;
  version?: number;
}

export interface SignatureConfigResponse {
  signTime?: string;
  signInfo?: any;
  types?: string[];
  configs: SignatureConfigItem[];
}

/**
 * beefintech.ifa.order.domain.entity.property.OrderSignInfo.Signature
 *
 * Signature
 */
export type Signature = {
  /**
   * 签名图片
   */
  signature?: string;
  /**
   * 签名时间
   */
  signedAt?: string;
  /**
   * 签名类型
   */
  type?: SignatureType;
  [property: string]: any;
};

export interface OrderSignatureDTO {
  /**
   * 签名地点
   */
  city?: City;
  /**
   * 订单号
   */
  orderId?: number;
  /**
   * 签名照片
   */
  photo?: string;
  /**
   * 签名(base64的图片)
   */
  signatures?: Signature[];
  [property: string]: any;
}

export type ResponseListOrderStatusCountVO = {
  code?: string;
  data?: OrderStatusCountVO[];
  message?: string;
  [property: string]: any;
};

/**
 * beefintech.ifa.order.domain.vo.OrderStatusCountVO
 *
 * OrderStatusCountVO
 */
export type OrderStatusCountVO = {
  count?: number;
  orderStatus?: DatumOrderStatus;
  subStatusCount?: OrderSubStatusCountVO[];
  [property: string]: any;
};

export enum DatumOrderStatus {
  Completed = "COMPLETED",
  ComplianceAudit = "COMPLIANCE_AUDIT",
  Draft = "DRAFT",
  Expired = "EXPIRED",
  ModifyInfo = "MODIFY_INFO",
  OrderAudit = "ORDER_AUDIT",
  Signature = "SIGNATURE",
}

/**
 * 订单状态枚举（用于 count-by-status 接口）
 */
export enum ResponseOrderStatus {
  All = "ALL",
  Completed = "COMPLETED",
  ComplianceAudit = "COMPLIANCE_AUDIT",
  Draft = "DRAFT",
  Expired = "EXPIRED",
  ModifyInfo = "MODIFY_INFO",
  OrderAudit = "ORDER_AUDIT",
  OrderAuditCra = "ORDER_AUDIT_CRA",
  PendingAllocation = "PENDING_ALLOCATION",
  Signature = "SIGNATURE",
}

/**
 * beefintech.ifa.order.domain.vo.OrderStatusCountVO.OrderSubStatusCountVO
 *
 * OrderSubStatusCountVO
 */
export type OrderSubStatusCountVO = {
  count?: number;
  orderStatus?: SubStatusCountOrderStatus;
  [property: string]: any;
};

export enum SubStatusCountOrderStatus {
  ModifyInfoPending = "MODIFY_INFO_PENDING",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingReview = "PENDING_REVIEW",
  PendingSignature = "PENDING_SIGNATURE",
  PendingSubmission = "PENDING_SUBMISSION",
  RiskAssessmentAuditStatusPending = "RISK_ASSESSMENT_AUDIT_STATUS_PENDING",
  PendingSubmit = "PENDING_SUBMIT",
  PendingFirstAudit = "PENDING_FIRST_AUDIT",
  PendingComplianceAudit = "PENDING_COMPLIANCE_AUDIT",
  PendingModificationCra = "PENDING_MODIFICATION_CRA",
}

/**
 * 订单状态数量响应（用于 count-by-status 接口）
 */
export interface OrderStatusCountResponse {
  count?: number;
  orderStatus?: ResponseOrderStatus;
  subStatusCount?: OrderSubStatusCountResponse[];
  [property: string]: any;
}

/**
 * 订单子状态数量响应（用于 count-by-status 接口）
 */
export interface OrderSubStatusCountResponse {
  count?: number;
  orderStatus?: SubStatusCountOrderStatus;
  [property: string]: any;
}

export type ComplianceAuditDTO = {
  complianceAuditStatus?: ComplianceAuditStatus | ComplianceRecheckStatus;
  orderId?: number;
  reason?: string;
  [property: string]: any;
};

/**
 *
 * OrderChangeVO
 */
export type OrderChangeVO = {
  /**
   * 变更详情
   */
  changeDetails?: OrderChangeDetailVO[];
  /**
   * 变更时间
   */
  changeTime?: string;
  /**
   * 变更发起人ID
   */
  creatorId?: number;
  /**
   * 变更发起人姓名
   */
  creatorName?: string;
  [property: string]: any;
};

/**
 *
 * OrderChangeDetailVO
 */
export type OrderChangeDetailVO = {
  field?: string;
  fieldName?: string;
  id?: number;
  newValue?: string;
  oldValue?: string;
  [property: string]: any;
};

/**
 * OrderCra
 */
export type OrderCra = {
  /**
   * 答案
   */
  answer?: FormData[];
  /**
   * CRA版本
   */
  craVersion?: string;
  /**
   * 客戶已投保的保單
   */
  insuredPolicy?: InsuredPolicy;
  /**
   * CRA ID
   */
  orderCraId?: number;
  auditReason?: string;
  auditStatus?: string;
  /**
   * 訂單ID
   */
  orderId?: number;
  [property: string]: any;
};

/**
 * aml结果
 */
export enum AmlResult {
  NoConcern = "NO_CONCERN",
  WithConcern = "WITH_CONCERN",
}

/**
 * beefintech.ifa.order.domain.entity.property.FormData
 *
 * FormData
 */
export type FormData = {
  /**
   * 选项/输入 列表
   */
  items?: FormDataItem[];
  questionNumber?: string;
  /**
   * 题号
   */
  question?: string;
  /**
   * 类型
   */
  type?: FinancialAnalysisType;
  [property: string]: any;
};

/**
 * beefintech.ifa.order.domain.entity.property.OrderFna.FormDataItem
 *
 * FormDataItem
 */
export type FormDataItem = {
  /**
   * 选项/输入 编码
   */
  key?: string;
  /**
   * 选项/输入 值
   * - CHECKBOX/RADIO: boolean
   * - INPUT: string
   * - AMOUNT: any
   */
  value?: boolean | string | { [key: string]: any };
  [property: string]: any;
};

/**
 * 财务分析表单数据类型
 */
export enum FinancialAnalysisType {
  Amount = "AMOUNT",
  Checkbox = "CHECKBOX",
  Input = "INPUT",
  Radio = "RADIO",
}

/**
 * beefintech.ifa.order.domain.entity.property.OrderCra.HighPremiumOrder
 *
 * HighPremiumOrder
 */
export type HighPremiumOrder = {
  /**
   * 年缴保费
   */
  annualPremium?: number;
  /**
   * 投保时间
   */
  applicationTime?: string;
  /**
   * 保险公司
   */
  companyCode?: string;
  /**
   * 保险公司名称
   */
  companyName?: string;
  /**
   * 货币
   */
  currency?: Currency;
  /**
   * 是否内部订单
   */
  isInternal?: boolean;
  /**
   * 一次性缴付保费（含预缴）
   */
  oneTimePremium?: number;
  /**
   * 订单编号
   */
  orderNo?: string;
  /**
   * 保单号码
   */
  policyNo?: string;
  /**
   * 状态
   */
  status?: string;
  [property: string]: any;
};

export type InsuredPolicy = HighPremiumOrder[];

export class OrderCraDTO {
  answer?: FormData[];
  highPremiumOrders?: HighPremiumOrder[];
  orderId?: number;
  /**
   * 业务代表签名图片（base64格式）
   */
  signatureImage?: string;
}

/**
 * AuditOrderDTO - 审核订单请求
 */
export interface AuditOrderDTO {
  /**
   * aml备注
   */
  amlRemark?: string;
  /**
   * aml结果
   */
  amlResult?: AmlResult;
  /**
   * 订单审核状态
   */
  orderAuditStatus?: OrderAuditStatus;
  /**
   * 订单ID
   */
  orderId?: number;
  /**
   * 原因说明
   */
  reason?: string;
  [property: string]: any;
}

/**
 * AmlAuditDTO - AML审核请求
 */
export interface AmlAuditDTO {
  /**
   * aml备注
   */
  amlRemark?: string;
  /**
   * aml结果
   */
  amlResult?: AmlResult;
  orderId?: number;
  [property: string]: any;
}

/**
 * 初审/订单审核状态
 */
export enum AuditStatus {
  Approved = "APPROVED",
  ConfirmationPendingReview = "CONFIRMATION_PENDING_REVIEW",
  Initial = "INITIAL",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingModification = "PENDING_MODIFICATION",
  PendingReview = "PENDING_REVIEW",
  Rejected = "REJECTED",
}

/**
 * 货币
 */
export enum Currency {
  Aud = "AUD",
  CAD = "CAD",
  Chf = "CHF",
  Cny = "CNY",
  Eur = "EUR",
  Gbp = "GBP",
  Hkd = "HKD",
  Jpy = "JPY",
  Mop = "MOP",
  Nzd = "NZD",
  Sgd = "SGD",
  Usd = "USD",
}

/**
 * 投保人类型
 */
export enum PolicyholderType {
  Company = "COMPANY",
  Individual = "INDIVIDUAL",
}

/**
 * beefintech.ifa.order.domain.vo.OrderCraVO.HighPremiumOrderVO
 */
export interface HighPremiumOrderVO {
  /**
   * 年缴保费
   */
  annualPremium?: number;
  /**
   * 投保时间
   */
  applicationTime?: string;
  /**
   * 保险公司
   */
  companyCode?: string;
  /**
   * 保险公司名称
   */
  companyName?: string;
  /**
   * 货币
   */
  currency?: Currency;
  /**
   * 是否内部订单
   */
  isInternal?: boolean;
  /**
   * 一次性缴付保费（含预缴）
   */
  oneTimePremium?: number;
  /**
   * 订单编号
   */
  orderNo?: string;
  /**
   * 保单号码
   */
  policyNo?: string;
  /**
   * 状态
   */
  status?: string;
  [property: string]: any;
}

/**
 * TODO 風險評估问卷結果
 */
export interface InsuredPolicyVO {
  /**
   * 年繳總保費
   */
  annualTotalPremium?: number;
  /**
   * 投保人已投保的保單清單
   */
  highPremiumOrders?: HighPremiumOrderVO[];
  /**
   * 一次性繳付保費(含預繳)
   */
  totalOneTimePayment?: number;
  [property: string]: any;
}

/**
 * 订单审核详情
 */
export interface OrderAuditDetailVO {
  /**
   * aml备注
   */
  amlRemark?: string;
  /**
   * 風險評估(基本審查)
   */
  amlResult?: AmlResult;
  /**
   * 初审备注
   */
  initialAuditRemark?: string;
  /**
   * 初审结果
   */
  initialAuditStatus?: AuditStatus;
  /**
   * TODO 風險評估问卷結果
   */
  insuredPolicy?: InsuredPolicyVO;
  /**
   * 订单审核状态
   */
  orderAuditStatus?: OrderAuditStatus;
  /**
   * 订单信息
   */
  orderId?: number;
  /**
   * 订单号
   */
  orderNo?: string;
  /**
   * 订单状态
   */
  orderStatus?: OrderStatus;
  /**
   * 投保人
   */
  policyholder?: PolicyholderVO;
  /**
   * 投保人类型
   */
  policyholderType?: PolicyholderType;
  /**
   * 保单号
   */
  policyNo?: string;
  /**
   * 订单产品计划
   */
  productPlan?: OrderProductPlanVO;
  /**
   * 合规审核状态
   */
  riskAssessmentAuditStatus?: RiskAssessmentAuditStatus;

  /**
   * AML审核状态
   */
  amlAuditStatus?: AmlAuditStatus;

  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.OrderProcessFlowRecordVO
 * 订单流程记录响应
 */
export interface OrderProcessFlowRecordVO {
  flowRecords?: FlowRecord[];
  insuredName?: string;
  orderId?: number;
  orderNo?: string;
  orderStatus?: OrderStatus;
  policyholderName?: string;
  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.OrderProcessFlowRecordVO.FlowRecord
 * 流程记录
 */
export interface FlowRecord {
  /**
   * 最后修改的流程记录ID
   */
  lastModifiedFlowRecordId?: number;
  /**
   * 是否有修改记录
   */
  canViewChangeHistory?: boolean;
  /**
   * 创建时间
   */
  createTime?: string;
  /**
   * 创建人ID
   */
  creatorId?: number;
  /**
   * 创建人名称
   */
  creatorName?: string;
  /**
   * 描述
   */
  description?: string;
  /**
   * 流程记录ID
   */
  flowRecordId?: number;
  /**
   * 新订单审核状态
   */
  newOrderAuditStatus?: OrderAuditStatus;
  /**
   * 新订单状态
   */
  newOrderStatus?: OrderStatus;
  /**
   * 新风险评估审核状态
   */
  newRiskAssessmentAuditStatus?: RiskAssessmentAuditStatus;
  /**
   * 旧订单审核状态
   */
  oldOrderAuditStatus?: OrderAuditStatus;
  /**
   * 旧订单状态
   */
  oldOrderStatus?: OrderStatus;
  /**
   * 旧风险评估审核状态
   */
  oldRiskAssessmentAuditStatus?: RiskAssessmentAuditStatus;
  /**
   * 签名URL地址
   */
  signature?: string;
  /**
   * 签名时间
   */
  signTime?: string;
  [property: string]: any;
}

/**
 * 查阅批改内容-WEB 请求参数
 */
export interface OrderChangeHistoryWebRequest {
  /**
   * 证件号（可选）
   */
  idCard?: string;
  /**
   * 分享码
   */
  shareCode: string;
  [property: string]: any;
}

/**
 * 订单审核-客户确认修改-WEB 请求参数
 * ConfirmationAuditDTO
 */
export interface ConfirmOrderChangeWebRequest {
  /**
   * 证件号（可选）
   */
  idCard?: string;
  /**
   * 分享码（可选）
   */
  shareCode?: string;
  /**
   * 客户签名
   */
  signature?: string;
  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.OrderChangeVO
 * 订单变更信息-WEB端
 */
export interface OrderChangeWebVO {
  /**
   * 变更详情
   */
  changeDetails?: OrderChangeDetailVO[];
  /**
   * 变更时间
   */
  changeTime?: string;
  /**
   * 变更发起人ID
   */
  creatorId?: number;
  /**
   * 变更发起人姓名
   */
  creatorName?: string;
  /**
   * IA编码
   */
  iaCode?: string;
  /**
   * 是否验证身份证后四位成功
   */
  success?: boolean;
  /**
   * 订单号
   */
  orderNo?: string;
  /**
   * 租户名称
   */
  tenantName?: string;
  /**
   * 投保人中文姓
   */
  policyholderChineseSurname?: string;
  /**
   * 投保人中文名
   */
  policyholderChineseGivenName?: string;
  /**
   * 投保人英文姓
   */
  policyholderEnglishSurname?: string;
  /**
   * 投保人英文名
   */
  policyholderEnglishGivenName?: string;
  /**
   * 投保人姓名（兼容旧字段）
   */
  policyholderChineseName?: string;
  /**
   * 投保人英文姓名（兼容旧字段）
   */
  policyholderEnglishName?: string;
  /**
   * 投保人性别
   */
  policyholderGender?: PolicyholderGender;
  /**
   * 投保人称谓
   */
  policyholderSalutation?: PolicyholderGender;
  /**
   * 签名时间
   */
  signTime?: string;
  /**
   * 签名数据
   */
  signature?: string;
  [property: string]: any;
}

export enum PolicyholderGender {
  Female = "FEMALE",
  Male = "MALE",
  Unknown = "UNKNOWN",
}

/**
 * AML 搜尋结果
 * beefintech.ifa.aml.domain.vo.AmlSearchResultVO
 */
export interface AmlSearchResultVO {
  /**
   * 生日
   */
  birthday?: string;
  /**
   * 国家
   */
  country?: string;
  /**
   * 性别
   */
  gender?: Gender;
  /**
   * 姓名
   */
  name?: string;
  /**
   * 资源ID
   */
  resourceId?: string;
  /**
   * 匹配分数
   */
  score?: number;
  [property: string]: any;
}

/**
 * AML 查询记录
 * beefintech.ifa.aml.domain.vo.AmlSearchRecordVO
 */
export interface AmlSearchRecordVO {
  /**
   * 记录ID
   */
  recordId?: number;

  createTime?: string;
  /**
   * AML 搜索结果列表
   */
  results?: AmlSearchResultVO[];
  [property: string]: any;
}

/**
 * AML 取消 DTO
 * AmlCancelDTO
 */
export interface AmlCancelDTO {
  /**
   * 订单ID
   */
  orderId?: number;
  /**
   * 资源ID
   */
  resourceId?: string;
  [property: string]: any;
}

/**
 * 订单表单 PDF 下载请求
 * OrderFormPdfRequest
 */
export interface OrderFormPdfRequest {
  /**
   * 表单类型
   * FNA: FNA
   * CRA: CRA
   */
  formType: string;
  /**
   * 订单ID
   */
  orderId: number;
  /**
   * 流程记录ID
   */
  flowRecordId?: number;
  [property: string]: any;
}

/**
 * CRA 风险评估项目类型
 */
export enum CraProject {
  CountryOrRegionalRisk = "COUNTRY_OR_REGIONAL_RISK",
  CustomerRisk = "CUSTOMER_RISK",
  DistributionOrDeliveryRisk = "DISTRIBUTION_OR_DELIVERY_RISK",
  FinalAmlCftRiskAssessment = "FINAL_AML_CFT_RISK_ASSESSMENT",
  ProductServiceRisk = "PRODUCT_SERVICE_RISK",
}

/**
 * CRA 风险评估结果
 */
export enum CraRiskResult {
  HighRisk = "HIGH_RISK",
  LowRisk = "LOW_RISK",
  MediumRisk = "MEDIUM_RISK",
  NoRisk = "NO_RISK",
}

/**
 * beefintech.ifa.order.domain.vo.CraResultVO
 * CRA 风险评估结果响应
 */
export interface CraResultVO {
  /**
   * 评估项目类型
   */
  project?: CraProject;
  /**
   * 评估原因说明
   */
  reason?: string;
  /**
   * 风险评估结果
   */
  result?: CraRiskResult;
  [property: string]: any;
}

// ========== 新增：订单列表 VO（Spring Data 分页版本）==========

/**
 * 了解途径枚举
 */
export enum Source {
  FriendRecommendation = "FRIEND_RECOMMENDATION",
  InsuranceCompanyOfficialWebsite = "INSURANCE_COMPANY_OFFICIAL_WEBSITE",
  NewsMediaAdvertising = "NEWS_MEDIA_ADVERTISING",
  Other = "OTHER",
  PendingConfirmation = "PENDING_CONFIRMATION",
}

/**
 * 订单来源枚举
 */
export enum OrderSource {
  SmartH5Online = "SMART_H5_ONLINE",
  TrOnline = "TR_ONLINE",
}

/**
 * OrderProductPlanListVO
 * 订单产品计划 VO（列表页使用）
 */
export interface OrderProductPlanListVO {
  /**
   * 申请客户特殊首年保费折扣
   */
  applySpecialFirstYearDiscount?: boolean;
  /**
   * 产品分类编码
   */
  categoryCode?: string;
  /**
   * 产品分类名称
   */
  categoryName?: string;
  /**
   * 公司码
   */
  companyCode?: string;
  /**
   * 公司名称
   */
  companyName?: string;
  /**
   * 供款年限
   */
  contributionYears?: string;
  /**
   * 币种
   */
  currency?: Currency;
  /**
   * 是否保費融資
   */
  isPremiumFinanced?: boolean;
  /**
   * 是否预缴
   */
  isPrepaid?: boolean;
  /**
   * 保险公司 logo
   */
  logo?: string;
  /**
   * 缴费频率
   */
  paymentFrequency?: PaymentFrequency;
  /**
   * 每期保费
   */
  premiumPerPeriod?: number;
  /**
   * 预缴额(含首期)
   */
  prepaidAmountInclFirst?: number;
  /**
   * 产品码
   */
  productCode?: string;
  /**
   * 产品名称
   */
  productName?: string;
  /**
   * 保障期
   */
  protectionPeriod?: string;
  /**
   * 区域
   */
  region?: string;
  /**
   * 附加险产品名称
   */
  riderNames?: string[];
  /**
   * 客戶實際投保特殊保費折扣
   */
  specialFirstYearDiscountRate?: number;
  /**
   * 保额
   */
  sumAssured?: number;
  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.OrderListVO
 *
 * OrderListVO
 */
export interface OrderListVO {
  /**
   * 保单文件状态
   */
  policyFileStatus?: PolicyFileStatus;
  /**
   * 业务代表中文姓名
   */
  advisorChineseName?: string;
  /**
   * 业务代表员工号
   */
  advisorEmpNo?: string;
  /**
   * 业务代表英文姓名
   */
  advisorEnglishName?: string;
  /**
   * 业务代表IA Code
   */
  advisorIaCode?: string;
  /**
   * 业务代表职级名称
   */
  advisorJobRankName?: string;
  /**
   * 业务代表手机号
   */
  advisorPhone?: string;
  /**
   * 业务代表团队名称
   */
  advisorTeamName?: string;
  /**
   * 业务代表团队ID
   */
  advisorTid?: number;
  /**
   * 业务代表
   */
  advisorUid?: number;
  /**
   * AML审核状态
   */
  amlAuditStatus?: AmlAuditStatus;
  /**
   * 投保日期
   */
  applyDate?: string;
  /**
   * 会面信息
   */
  appointmentInfo?: OrderAppointmentInfo;
  /**
   * 冷靜期截止日期
   */
  coolingOffPeriodEndDate?: string;
  /**
   * 取消原因
   */
  expiredReason?: string;
  /**
   * 取消时间
   */
  expiredTime?: string;
  /**
   * 受保人出生日期
   */
  insuredBirthdate?: string;
  /**
   * 受保人中文姓名
   */
  insuredChineseName?: string;
  /**
   * 受保人英文姓名
   */
  insuredEnglishName?: string;
  /**
   * 受保人ID
   */
  insuredId?: number;
  /**
   * 受保人与投保人的关系
   */
  insuredRelationship?: InsuredRelationship;
  /**
   * 受保人类型
   */
  insuredType?: Type;
  /**
   * 保司保单状态
   */
  insurerPolicyStatus?: string;
  /**
   * 意向产品
   */
  intendedProducts?: OrderIntendedProduct[];
  /**
   * 订单审核状态
   */
  orderAuditStatus?: OrderAuditStatus;
  /**
   * 订单归属
   */
  orderBelong?: OrderBelong;
  /**
   * 订单ID
   */
  orderId?: number;
  /**
   * 订单号
   */
  orderNo?: string;
  /**
   * 簽單類型
   */
  orderSignType?: OrderSignType;
  /**
   * 订单状态
   */
  orderStatus?: OrderStatus;
  /**
   * 繳費完成日期
   */
  paymentCompleteDate?: string;
  /**
   * 保单年度
   */
  policyAnnual?: number;
  /**
   * 保單生效日期
   */
  policyEffectiveDate?: string;
  /**
   * 投保人出生日期
   */
  policyholderBirthdate?: string;
  /**
   * 投保人中文姓名
   */
  policyholderChineseName?: string;
  /**
   * 投保人英文姓名
   */
  policyholderEnglishName?: string;
  /**
   * 投保人ID
   */
  policyholderId?: number;
  /**
   * 投受益人信息
   */
  policyholderType?: Type;
  /**
   * 繕發日期
   */
  policyIssueDate?: string;
  /**
   * 保单号
   */
  policyNo?: string;
  /**
   * 保单状态
   */
  policyStatus?: PolicyStatus;
  /**
   * 产品信息
   */
  productPlans?: OrderProductPlanListVO[];
  /**
   * 转介人中文姓名
   */
  referrerChineseName?: string;
  /**
   * 转介人员工号
   */
  referrerEmpNo?: string;
  /**
   * 转介人英文姓名
   */
  referrerEnglishName?: string;
  /**
   * 顾问职级名称
   */
  referrerJobRankName?: string;
  /**
   * 转介人手机号
   */
  referrerPhone?: string;
  /**
   * 转介人团队名称
   */
  referrerTeamName?: string;
  /**
   * 转介人团队ID
   */
  referrerTid?: number;
  /**
   * 转介人ID
   */
  referrerUid?: number;
  /**
   * 是否重新签署
   */
  reSign?: boolean;
  /**
   * 风险评估审核状态
   */
  riskAssessmentAuditStatus?: RiskAssessmentAuditStatus;
  /**
   * CRA風險等級
   */
  riskClassResult?: RiskClassResult;
  /**
   * 签署时间
   */
  signTime?: string;
  /**
   * 订单来源
   */
  source?: OrderSource;
  /**
   * 提交时间
   */
  submitTime?: string;
  /**
   * 同時簽署的訂單
   */
  unionNo?: string;
  /**
   * 同時簽署的訂單
   */
  unionOrders?: UnionOrderVO[];
  [property: string]: any;
}

/**
 * beefintech.ifa.order.domain.vo.OrderListVO.UnionOrderVO
 *
 * UnionOrderVO
 */
export interface UnionOrderVO {
  orderId?: number;
  orderNo?: string;
  [property: string]: any;
}
/**
 * Pageable
 * Spring Data 分页参数
 */
export interface Pageable {
  /**
   * 偏移量
   */
  offset?: number;
  /**
   * 是否分页
   */
  paged?: boolean;
  /**
   * 页码
   */
  pageNumber?: number;
  /**
   * 页大小
   */
  pageSize?: number;
  /**
   * 排序参数
   */
  sort?: Sort[];
  /**
   * 是否不分页
   */
  unpaged?: boolean;
  [property: string]: any;
}

/**
 * Sort
 * Spring Data 排序参数
 */
export interface Sort {
  /**
   * 是否升序
   */
  ascending?: boolean;
  /**
   * 是否降序
   */
  descending?: boolean;
  /**
   * 排序方向
   */
  direction?: Direction;
  /**
   * 是否忽略大小写
   */
  ignoreCase?: boolean;
  /**
   * NULL 处理方式
   */
  nullHandling?: NullHandling;
  /**
   * 排序字段
   */
  property?: string;
  [property: string]: any;
}

/**
 * 排序方向枚举
 */
export enum Direction {
  Asc = "ASC",
  Desc = "DESC",
}

/**
 * NULL 处理枚举
 */
export enum NullHandling {
  Native = "NATIVE",
  NullsFirst = "NULLS_FIRST",
  NullsLast = "NULLS_LAST",
}

/**
 * PageInfo
 * 分页信息对象（固定结构）
 */
export interface PageInfo {
  /**
   * 页大小
   */
  size?: number;
  /**
   * 当前页码（从0开始）
   */
  number?: number;
  /**
   * 总数据条数
   */
  totalElements?: number;
  /**
   * 总页数
   */
  totalPages?: number;
  [property: string]: any;
}

/**
 * PageOrderListVO
 * 订单列表分页响应（固定结构）
 */
export interface PageOrderListVO {
  /**
   * 数据列表
   */
  content?: OrderListVO[];
  /**
   * 分页信息
   */
  page?: PageInfo;
  [property: string]: any;
}

// ========== Smart Order 状态数量统计 ==========

/**
 * Smart Order 主状态枚举
 */
export enum SmartOrderMainStatus {
  Appointment = "APPOINTMENT",
  Signature = "SIGNATURE",
  Signed = "SIGNED",
  Canceled = "CANCELED",
}

/**
 * Smart Order 子状态枚举
 */
export enum SmartOrderSubStatus {
  All = "ALL",
  Draft = "DRAFT",
  PendingAllocation = "PENDING_ALLOCATION",
  PendingConfirmation = "PENDING_CONFIRMATION",
  PendingSignature = "PENDING_SIGNATURE",
}

/**
 * 子状态数量 VO
 * SmartOrderSubStatusCountVO
 */
export interface SmartOrderSubStatusCountVO {
  /**
   * 订单子状态
   */
  orderStatus?: string;
  /**
   * 数量
   */
  count?: number;
  [property: string]: any;
}

/**
 * 主状态数量 VO
 * SmartOrderStatusCountVO
 */
export interface SmartOrderStatusCountVO {
  /**
   * 订单主状态
   */
  orderStatus?: string;
  /**
   * 数量
   */
  count?: number;
  /**
   * 子状态数量列表
   */
  subStatusCount?: SmartOrderSubStatusCountVO[];
  [property: string]: any;
}

/**
 * 意向产品来源枚举
 */
export enum ProductSource {
  PendingConfirmation = "PENDING_CONFIRMATION",
  FriendRecommendation = "FRIEND_RECOMMENDATION",
  InsuranceCompanyOfficialWebsite = "INSURANCE_COMPANY_OFFICIAL_WEBSITE",
  NewsMediaAdvertising = "NEWS_MEDIA_ADVERTISING",
  Other = "OTHER",
}

/**
 * 意向产品 DTO
 * OrderIntendedProduct
 */
export interface OrderIntendedProduct {
  /** 产品编码 */
  productCode?: string;
  /** 产品名称 */
  productName?: string;
  /** 销售地区 */
  region?: string;
  /** 了解途径 */
  source?: ProductSource;
  /** 申请客户特殊首年保费折扣 */
  specialFirstYearOffer?: boolean;
  [property: string]: any;
}

/**
 * 预约信息 DTO
 * OrderAppointmentInfo
 */
export interface OrderAppointmentInfo {
  /** 是否公司分配 */
  isCompanyAssigned?: boolean;
  /** 会面日期 YYYY-MM-DD */
  appointmentDate?: string;
  /** 会面开始时间 HH:mm */
  appointmentStartTime?: string;
  /** 会面结束时间 HH:mm */
  appointmentEndTime?: string;
  /** 会面地点 */
  appointmentLocation?: string;
  /** 是否需要会议室 */
  isMeetingRoom?: boolean;
  /** 会议室 */
  meetingRoom?: string;
  /** 会面地址（其他地点时填写） */
  appointmentAddress?: string;
  /** 备注 */
  remark?: string;
  /** 确认会面日期 */
  confirmAppointmentDate?: string;
  /** 确认会面开始时间 */
  confirmAppointmentStartTime?: string;
  /** 确认会面结束时间 */
  confirmAppointmentEndTime?: string;
  [property: string]: any;
}

/**
 * Smart Order 创建/更新请求 DTO
 * POST /api/v1/smart/order/create
 */
export interface SmartOrderBasicDTO {
  /** 草稿状态标志 - 必填 */
  draft: boolean;
  /** 订单ID（编辑时使用） */
  orderId?: number;
  /** 投保人 */
  policyholder?: PolicyholderDTO;
  /** 第二投保人 */
  secondPolicyholder?: PolicyholderDTO;
  /** 受保人 */
  insured?: InsuredDTO;
  /** 第二受保人 */
  secondInsured?: InsuredDTO;
  /** 受益人列表 */
  beneficiaries?: BeneficiaryDTO[];
  /** 是否法定受益人 */
  legalBeneficiary?: boolean;
  /** 财务资料 */
  financialData?: FinancialDataDTO;
  /** 顾问/业务代表ID */
  advisorUid?: number;
  /** 预约信息 */
  appointmentInfo?: OrderAppointmentInfo;
  /** 意向产品列表 */
  intendedProducts?: OrderIntendedProduct[];
  [property: string]: any;
}

/**
 * Smart Order 创建响应
 */
export interface SmartOrderCreateResponse {
  /** 订单ID */
  orderId?: number;
  [property: string]: any;
}

/**
 * 分配业务代表请求 DTO
 * PUT /api/v1/order/assign-advisor
 */
export interface AssignAdvisorDTO extends OrderAppointmentInfo {
  /** 订单ID - 必填 */
  orderId: number;
  /** 业务代表UID - 公司分配时传null */
  advisorUid?: number | null;
}

/**
 * 分配业务代表请求参数（dev 版本）
 * OrderAppointmentInfoDTO
 */
export interface AssignAdvisorRequest {
  /** 订单ID */
  orderId: number;
  /** 业务代表用户ID */
  advisorUid?: number;
  /** 会面地址 */
  appointmentAddress?: string;
  /** 会面日期 */
  appointmentDate?: string;
  /** 会面开始时间 */
  appointmentStartTime?: string;
  /** 会面结束时间 */
  appointmentEndTime?: string;
  /** 会面地点 */
  appointmentLocation?: string;
  /** 确认会面日期 */
  confirmAppointmentDate?: string;
  /** 确认会面开始时间 */
  confirmAppointmentStartTime?: string;
  /** 确认会面结束时间 */
  confirmAppointmentEndTime?: string;
  /** 是否公司分配 */
  isCompanyAssigned?: boolean;
  /** 是否需要会议室 */
  isMeetingRoom?: boolean;
  /** 会议室 */
  meetingRoom?: string;
  /** 备注 */
  remark?: string;
  [property: string]: any;
}

/**
 * Smart Order 更新投保人请求 DTO
 * PUT /api/v1/smart/order/update-policyholder
 */
export interface SmartPolicyholderDTO {
  /** 是否同步数据给待签署状态的联合订单 */
  syncToUnion?: boolean;
  /** 订单ID - 必填 */
  orderId: number;
  /** 草稿状态标志 */
  draft?: boolean;
  /** 是否提交 */
  submit?: boolean;
  /** 投保人ID */
  policyholderId?: number;
  /** 基本信息 */
  basic?: BasicInfo;
  /** 个人信息 */
  personal?: PersonalInfo;
  /** 家庭成员 */
  familyMembers?: FamilyMember[];
  /** 工作信息 */
  work?: WorkInfo;
  /** 税务信息 */
  tax?: TaxInfo;
  /** 其他信息（包含 isVulnerableCustomer, isProfessionalInvestor, remark） */
  other?: PolicyholderOther;
  /** 是否第二投保人 */
  isSecondPolicyholder?: boolean;
  /** 与受保人关系 */
  relationship?: BeneficiaryRelationship;
  [property: string]: any;
}

/**
 * Smart Order 更新预约信息请求 DTO
 * PUT /api/v1/smart/order/update-appointment
 */
export interface SmartOrderAppointmentDTO {
  /** 订单ID - 必填 */
  orderId: number;
  /** 业务代表UID */
  advisorUid?: number;
  /** 是否公司分配 */
  isCompanyAssigned?: boolean;
  /** 会面日期 */
  appointmentDate?: string;
  /** 会面开始时间 */
  appointmentStartTime?: string;
  /** 会面结束时间 */
  appointmentEndTime?: string;
  /** 会面地点 */
  appointmentLocation?: string;
  /** 是否需要会议室 */
  isMeetingRoom?: boolean;
  /** 会议室 */
  meetingRoom?: string;
  /** 会面地址 */
  appointmentAddress?: string;
  /** 备注 */
  remark?: string;
  /** 确认会面日期 */
  confirmAppointmentDate?: string;
  /** 确认会面开始时间 */
  confirmAppointmentStartTime?: string;
  /** 确认会面结束时间 */
  confirmAppointmentEndTime?: string;
  [property: string]: any;
}

/**
 * Smart Order 更新意向产品请求 DTO
 * PUT /api/v1/smart/order/update-intended-product
 */
export interface OrderIntendedProductDTO {
  /** 订单ID - 必填 */
  orderId: number;
  /** 意向产品列表 */
  intendedProducts?: OrderIntendedProduct[];
}

/**
 * Smart Order 更新受保人请求 DTO
 * PUT /api/v1/smart/order/update-insured
 */
export interface SmartInsuredDTO {
  /** 是否同步数据给待签署状态的联合订单 */
  syncToUnion?: boolean;
  /** 订单ID - 必填 */
  orderId: number;
  /** 草稿状态标志 */
  draft?: boolean;
  /** 是否提交 */
  submit?: boolean;
  /** 受保人ID */
  insuredId?: number;
  /** 与投保人关系 */
  relationship?: BeneficiaryRelationship;
  /** 基本信息 */
  basic?: BasicInfo;
  /** 个人信息 */
  personal?: PersonalInfo;
  /** 税务信息 */
  tax?: TaxInfo;
  /** 工作信息 */
  work?: WorkInfo;
  /** 其他信息（包含 remark） */
  other?: InsuredOther;
  [property: string]: any;
}

export type OrderFnaDTO = {
  /**
   * 推荐的保險產品
   */
  chooseProducts?: ChooseProduct[];
  /**
   * 财务需要分析
   */
  financialAnalysis?: FormData[];
  /**
   * 閣下是否有任何現有的保單被用作"保費融資"的抵押品或以其他方式質押或轉讓作為貸款工具的抵押品
   */
  hasExistingPolicyAsMortgage?: boolean;
  /**
   * 閣下是否打算運用"保費融資"安排此保單
   */
  isPremiumFinanced?: boolean;
  /**
   * 是否只能提供一款保险产品
   */
  onlyProvideOneProduct?: boolean;
  /**
   * 是否只能提供一家保險公司建議之原因
   */
  onlyProvideOneProductReason?: string;
  orderId: number;
  /**
   * 現有保費融資的抵押保單詳情
   */
  premiumFinancing?: PremiumFinancing[];
  /**
   * 建议及原因
   */
  suggestionsAndReasons?: FormData[];
  /**
   * 转保声明
   */
  transferInsuranceDeclaration?: FormData[];
  [property: string]: any;
};

/**
 * Smart Order 更新财务资料请求 DTO
 * PUT /api/v1/smart/order/update-financial
 */
export interface SmartOrderFinancialDTO {
  /** 订单ID - 必填 */
  orderId: number;
  /** 财务资料ID */
  financialId?: number;
  /** 是否需要填写财务资料 */
  isFinancialInfoRequired?: boolean;
  /** 过去2年的平均每月收入 */
  income?: MonthlyIncome;
  /** 过去2年的平均每月支出 */
  expense?: MonthlyExpense;
  /** 流动资产 */
  currentAssets?: CurrentAssets;
  /** 非流动资产 */
  nonCurrentAssets?: NonCurrentAssets;
  /** 负债 */
  liabilities?: Liabilities;
  /** 现行保障 */
  protection?: CurrentProtection;
}

/**
 * Smart Order 保險策略请求 DTO
 * PUT /api/v1/smart/order/save-cra-insured-policy
 */
export interface SmartInsuredPolicyDTO {
  /** 订单ID - 必填 */
  orderId: number;
  /** 高保費訂單列表 */
  highPremiumOrders?: HighPremiumOrder[];
  [property: string]: any;
}

/**
 * 保费承担能力计算请求 DTO
 * PUT /api/v1/order/premium-capacity
 */
export interface PremiumCapacityRequest {
  /** 订单ID - 必填 */
  orderId: number;
  /** 财务分析数据 */
  financialAnalysis: any;
}

/**
 * 保费承担能力计算响应 VO
 */
export interface PremiumCapacityResponse {
  /** 每年可動用收入 */
  annualDisposableIncome?: number;
  /** 流動資產 */
  liquidAssets?: number;
  /** 每年可動用收入願繳保費最高額 */
  maxAnnualPremium?: number;
  /** 流動資產願繳保費最高額 */
  maxLiquidAssetPremium?: number;
  /** 每月可動用收入願繳保費最高額 */
  maxMonthlyPremium?: number;
  /** 每月可動用收入 */
  monthlyDisposableIncome?: number;
  [property: string]: any;
}

/**
 * 保单维护请求 DTO
 * PUT /api/v1/order/maintenance-policy
 */
export interface OrderMaintenanceDTO {
  /**
   * 订单ID - 必填
   */
  orderId: number;
  /**
   * 保司保单状态
   */
  insurerPolicyStatus?: string;
  /**
   * 保单号 - 必填
   */
  policyNo: string;
  /**
   * 保单状态 - 必填
   */
  policyStatus: PolicyStatus;
  /**
   * 繕發日期 - 必填
   */
  policyIssueDate: string;
  /**
   * 投保日期
   */
  applyDate?: string;
  /**
   * 繳費完成日期
   */
  paymentCompleteDate?: string;
  /**
   * 保單生效日期
   */
  policyEffectiveDate?: string;
  /**
   * 冷靜期截止日期
   */
  coolingOffPeriodEndDate?: string;
  /**
   * 转入保单管理
   */
  transferred?: boolean;
  [property: string]: any;
}

/**
 * 繳費方式（首期）
 */
export enum InitialPaymentMethod {
  BANK_AUTO_TRANSFER = "BANK_AUTO_TRANSFER",
  BANK_CASH_RECEIPTS = "BANK_CASH_RECEIPTS",
  BANK_TRANSFER = "BANK_TRANSFER",
  CASH = "CASH",
  CASH_ORDER = "CASH_ORDER",
  CHEQUE = "CHEQUE",
  CREDIT_CARD = "CREDIT_CARD",
  DELAY_PAYMENT = "DELAY_PAYMENT",
  OTHER = "OTHER",
  TELEGRAPHIC_TRANSFER = "TELEGRAPHIC_TRANSFER",
  UNION_PAY = "UNION_PAY",
  UNKNOWN = "UNKNOWN",
}

/**
 * 繳費方式（續期）
 */
export enum RenewalPaymentMethod {
  BANK_DRAFT = "BANK_DRAFT",
  BANK_TRANSFER = "BANK_TRANSFER",
  BILL_PAY = "BILL_PAY",
  CASH = "CASH",
  CHEQUE = "CHEQUE",
  FPS = "FPS",
  ONLINE_BANKING_PAYMENT = "ONLINE_BANKING_PAYMENT",
  OTHER = "OTHER",
  PPS_APP = "PPS_APP",
  TELEGRAPHIC_TRANSFER = "TELEGRAPHIC_TRANSFER",
  THIRD_PARTY_PAYMENT = "THIRD_PARTY_PAYMENT",
  USER_CERTIFICATE = "USER_CERTIFICATE",
}

/**
 * 文件材料
 */
export interface Material {
  /** 上传者 */
  author?: string;
  /** 文件名称 */
  fileName?: string;
  /** 文件路径 */
  filePath?: string;
  /** 文件大小 */
  fileSize?: number;
  /** 标签 */
  tags?: string[];
  /** 上传时间 */
  uploadTime?: number;
  [property: string]: any;
}

/**
 * 首期缴费记录
 */
export interface OrderPaymentRecord {
  createTime?: string;
  creatorId?: number;
  /** 幣種 */
  currency?: Currency;
  id?: number;
  /** 订单ID */
  orderId?: number;
  /** 缴费日期 */
  paymentDate?: string;
  /** 缴费文件 */
  paymentFile?: Material;
  /** 繳費方式 */
  paymentMethod?: InitialPaymentMethod;
  /** 繳費期數 */
  paymentPeriod?: number;
  /** 缴费记录ID */
  paymentRecordId?: number;
  /** 確認實繳金額 */
  premiumPaid?: number;
  updaterId?: number;
  updateTime?: string;
  version?: number;
  [property: string]: any;
}

/**
 * 续期缴费记录
 */
export interface OrderPaymentRecordVO {
  /** 創建時間 */
  createTime?: string;
  /** 創建人員工編號 */
  creatorEmpNo?: string;
  /** 創建人IaCode */
  creatorIaCode?: string;
  /** 創建人ID */
  creatorId?: number;
  /** 創建人姓名 */
  creatorName?: string;
  /** 幣種 */
  currency?: Currency;
  orderId?: number;
  /** 缴费日期 */
  paymentDate?: string;
  /** 缴费文件 */
  paymentFile?: Material;
  /** 繳費方式 */
  paymentMethod?: RenewalPaymentMethod;
  /** 繳費期數 */
  paymentPeriod?: number;
  paymentRecordId?: number;
  /** 確認實繳金額 */
  premiumPaid?: number;
  [property: string]: any;
}

/**
 * 缴费记录 DTO（用于添加/更新）
 */
export interface OrderPaymentRecordDTO {
  /** 幣種 */
  currency: Currency;
  /** 訂單ID */
  orderId: number;
  /** 缴费文件 token */
  paymentFileToken?: string;
  /** 繳費方式 */
  paymentMethod: InitialPaymentMethod | RenewalPaymentMethod;
  /** 繳費期數 */
  paymentPeriod?: number;
  /** 缴费记录ID（更新时需要） */
  paymentRecordId?: number;
  /** 確認實繳金額 */
  premiumPaid: number;
  [property: string]: any;
}

/**
 * 審核記錄查詢請求參數
 */
export interface AuditRecordListRequest {
  /**
   * 審核類型（CRA_AUDIT / AML_AUDIT）
   */
  auditType: AuditRecordType;
  /**
   * 訂單ID
   */
  orderId: number;
}

/**
 * 審核記錄類型
 */
export enum AuditRecordType {
  CraAudit = "CRA_AUDIT",
  AmlAudit = "AML_AUDIT",
}

/**
 * 審核記錄 VO
 */
export interface AuditRecordVO {
  /**
   * 審核人工號
   */
  auditorEmpNo?: string;
  /**
   * 審核人姓名
   */
  auditorName?: string;
  /**
   * 審核人 uid
   */
  auditorUid?: number;
  /**
   * 原因說明
   */
  auditReason?: string;
  /**
   * 審核狀態
   */
  auditStatus?: AuditRecordStatus;
  /**
   * 審核時間
   */
  auditTime?: string;
  /**
   * 審核類型（CRA/AML）
   */
  auditType?: AuditRecordType;
  /**
   * 風險評估問卷結果列表
   */
  craResult?: CraResultItem[];
  id?: number;
  orderId?: number;
  [property: string]: any;
}

/**
 * 審核記錄狀態
 */
export enum AuditRecordStatus {
  Approved = "APPROVED",
  NoConcern = "NO_CONCERN",
  Rejected = "REJECTED",
  WithConcern = "WITH_CONCERN",
}

/**
 * CRA 結果項
 */
export interface CraResultItem {
  project?: CraProject;
  reason?: string;
  result?: CraRiskLevel;
  [property: string]: any;
}

/**
 * CRA 項目類型
 */
export enum CraProject {
  CountryOrRegionalRisk = "COUNTRY_OR_REGIONAL_RISK",
  CustomerRisk = "CUSTOMER_RISK",
  DistributionOrDeliveryRisk = "DISTRIBUTION_OR_DELIVERY_RISK",
  FinalAmlCftRiskAssessment = "FINAL_AML_CFT_RISK_ASSESSMENT",
  ProductServiceRisk = "PRODUCT_SERVICE_RISK",
}

/**
 * CRA 風險等級
 */
export enum CraRiskLevel {
  HighRisk = "HIGH_RISK",
  LowRisk = "LOW_RISK",
  MediumRisk = "MEDIUM_RISK",
}

/**
 * 創建審核記錄請求參數
 */
export interface CreateAuditRecordRequest {
  /**
   * 審核原因
   */
  auditReason?: string;
  /**
   * 審核狀態
   */
  auditStatus: AuditRecordStatus;
  /**
   * 審核類型（CRA/AML）
   */
  auditType: AuditRecordType;
  /**
   * 訂單 ID
   */
  orderId: number;
  [property: string]: any;
}

/**
 * 刪除審核記錄請求參數
 */
export interface DeleteAuditRecordRequest {
  /**
   * 審核類型（CRA_AUDIT/AML_AUDIT）
   */
  auditType: string;
  /**
   * 審核記錄 ID
   */
  id: number;
  /**
   * 訂單 ID
   */
  orderId: number;
}

/**
 * 交叉銷售確認數據 VO
 */
export interface CrossSellingConfirmationVO {
  /**
   * 當前訂單信息
   */
  currentOrder?: CurrentOrder;
  /**
   * 客戶歷史訂單列表
   */
  customerOrders?: CustomerOrder[];
  [property: string]: any;
}

/**
 * 當前訂單信息
 */
export interface CurrentOrder {
  /**
   * 業務代表信息
   */
  advisor?: UserInfo;
  /**
   * 客戶ID
   */
  customerId?: number;
  /**
   * 投保人身份證號
   */
  idNumber?: string;
  /**
   * 投保人身份證類型
   */
  idType?: IdType;
  /**
   * 受保人姓名
   */
  insured?: string;
  /**
   * 受保人英文姓名
   */
  insuredEnglishName?: string;
  /**
   * 主要產品列表
   */
  mainProducts?: string[];
  /**
   * 訂單號
   */
  orderNo?: string;
  /**
   * 投保人姓名
   */
  policyholder?: string;
  /**
   * 投保人英文姓名
   */
  policyholderEnglishName?: string;
  /**
   * 推薦人信息
   */
  referrer?: UserInfo;
  /**
   * 受保人與投保人關係
   */
  relationship?: BeneficiaryRelationship;
  [property: string]: any;
}

/**
 * 用戶信息（業務代表/推薦人）
 */
export interface UserInfo {
  /**
   * 部門
   */
  department?: string;
  /**
   * 員工編號
   */
  empNo?: string;
  /**
   * 英文姓名
   */
  englishName?: string;
  /**
   * 姓名
   */
  name?: string;
  /**
   * 職級
   */
  rank?: string;
  /**
   * 用戶ID
   */
  uid?: number;
  [property: string]: any;
}

/**
 * 客戶歷史訂單
 */
export interface CustomerOrder {
  /**
   * 業務代表信息
   */
  advisor?: UserInfo;
  /**
   * 保險公司名稱
   */
  companyName?: string;
  /**
   * 供款年期
   */
  contributionYears?: string;
  /**
   * 幣種
   */
  currency?: Currency;
  /**
   * 訂單號
   */
  orderNo?: string;
  /**
   * 繳費頻率
   */
  paymentFrequency?: PaymentFrequency;
  /**
   * 保單號
   */
  policyNo?: string;
  /**
   * 每期保費
   */
  premiumPerPeriod?: number;
  /**
   * 產品類別
   */
  productCategory?: string;
  /**
   * 產品名稱
   */
  productName?: string;
  /**
   * 產品地區
   */
  productRegion?: string;
  /**
   * 推薦人信息
   */
  referrer?: UserInfo;
  /**
   * 是否重簽
   */
  reSign?: boolean;
  /**
   * 簽署后提交時間
   */
  signTime?: string;
  [property: string]: any;
}

/**
 * 禁止簽署請求參數
 */
export interface CrossSellingRejectRequest {
  /**
   * 訂單ID
   */
  orderId: number;
  /**
   * 原因
   */
  reason?: string;
}

/**
 * 移交客戶請求參數（訂單狀態）
 */
export interface CrossSellingTransferRequest {
  /**
   * 新業務代表 UID
   */
  newAdvisorUid: number;
  /**
   * 訂單ID
   */
  orderId: number;
  /**
   * 原因
   */
  reason?: string;
}

/**
 * 移交客戶請求參數（交叉銷售狀態）
 */
export interface TransferCustomerRequest {
  /**
   * 客戶ID
   */
  customerId: number;
  /**
   * 新業務代表 UID
   */
  newAdvisorUid: number;
  /**
   * 原因
   */
  reason?: string;
}

/**
 * 受保人与投保人的关系
 */
export enum InsuredRelationship {
  Brother = "BROTHER",
  BrotherAndSister = "BROTHER_AND_SISTER",
  Child = "CHILD",
  Employee = "EMPLOYEE",
  Fiance = "FIANCE",
  Fiancee = "FIANCEE",
  GrandparentOrGrandchild = "GRANDPARENT_OR_GRANDCHILD",
  LegalGuardian = "LEGAL_GUARDIAN",
  Other = "OTHER",
  Parent = "PARENT",
  Self = "SELF",
  Sister = "SISTER",
  SisterAndBrother = "SISTER_AND_BROTHER",
  Spouse = "SPOUSE",
  Trustee = "TRUSTEE",
  Trustor = "TRUSTOR",
  Unidentified = "UNIDENTIFIED",
  Unknown = "UNKNOWN",
}

/**
 * 簽單類型
 */
export enum OrderSignType {
  DirectSignByRep = "DIRECT_SIGN_BY_REP",
  ReferralSign = "REFERRAL_SIGN",
}
